import { observer } from 'mobx-react-lite';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { authStore } from '@/stores/authStore';
import axiosInstance from '@/utils/axiosConfig';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

interface LocationState {
  returnTo?: string;
}

interface Application {
  id: string;
  name: string;
  slug: string;
}

interface OrganizationApplication {
  id: string;
  application: Application;
}

export const LoginPage = observer(() => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { returnTo } = (location.state as LocationState) || {};
  const { t } = useTranslation();
  
  // Get redirect parameter from URL if present
  const getRedirectUrl = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('redirect');
  }, []);

  // Process redirect URL directly to reduce complexity
  const handleRedirect = useCallback((redirectUrl: string | null) => {
    if (redirectUrl) {
      console.log('Redirecting to:', redirectUrl);
      window.location.href = redirectUrl;
      return true;
    }
    return false;
  }, []);
  
  // Fetch applications for the organization and perform redirection based on role
  const handleRoleBasedRedirect = useCallback(async () => {
    // If there's no user or no current organization, can't do role-based redirection
    if (!authStore.user || !authStore.currentOrganization) {
      return false;
    }
    
    console.log('Checking role-based redirect for:', authStore.currentOrganization);
    
    // Check if there's a specific redirect URL in the query parameters (highest priority)
    const redirectUrl = getRedirectUrl();
    if (redirectUrl) {
      return handleRedirect(redirectUrl);
    }
    
    // Get current role from the organization
    const userRole = authStore.currentOrganization.role;
    const orgId = authStore.currentOrganization.id;
    
    // Check role for role-based redirection
    if (userRole === 'MEMBER') {
      try {
        // For MEMBER role, fetch available applications and redirect to the first one
        const response = await axiosInstance.get(`/applications/organizations/${orgId}`);
        const orgApplications: OrganizationApplication[] = response.data || [];
        
        if (orgApplications.length > 0) {
          // Get first application's slug
          const appSlug = orgApplications[0].application.slug;
          return handleRedirect(`https://${appSlug}.sensorlab.gr`);
        }
        
        // If no applications, redirect to a default app
        console.warn('No applications found for organization, using default app');
        return handleRedirect('https://auth.sensorlab.gr');
      } catch (error) {
        console.error('Failed to fetch organization applications:', error);
        // Fallback to a default application
        return handleRedirect('https://auth.sensorlab.gr');
      }
    }
    
    // For OWNER and ADMIN roles, allow normal dashboard access
    return false;
  }, [getRedirectUrl, handleRedirect]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlError = params.get('error');
    if (urlError === 'max_sessions_reached') {
      authStore.setError('Maximum number of active sessions reached. Please sign out from another device.');
    }
  }, []);

  useEffect(() => {
    const checkActiveSession = async () => {
      // Skip if we've already checked or are currently checking
      if (sessionChecked || isCheckingSession) return;
      
      try {
        setIsCheckingSession(true);
        
        const redirectUrl = getRedirectUrl();
        
        // First, handle direct redirect URL if present
        if (redirectUrl && authStore.accessToken && !authStore.isTokenExpired) {
          // We have a valid token and redirect URL, redirect immediately
          handleRedirect(redirectUrl);
          return;
        }
        
        if (!authStore.accessToken || authStore.isTokenExpired) {
          // Only try to refresh if we have a cookie
          const cookies = document.cookie.split(';');
          const hasRefreshToken = cookies.some(c => c.trim().startsWith('refreshToken='));
          
          if (hasRefreshToken) {
            try {
              const response = await axiosInstance.post('/auth/refresh-token', {}, {
                withCredentials: true
              });

              if (response?.data) {
                const { accessToken, user } = response.data;
                authStore.setTokens(accessToken);
                authStore.setUser(user);
                
                // Set organization in an action to avoid MobX error
                await authStore.setInitialOrganization();
                
                // Check role-based redirection based on user data
                const shouldRedirect = await handleRoleBasedRedirect();
                if (shouldRedirect) {
                  return;
                }
                
                // Handle explicit redirect if present
                if (redirectUrl) {
                  handleRedirect(redirectUrl);
                  return;
                }
                
                // Otherwise navigate to dashboard for OWNER/ADMIN roles
                navigate('/dashboard');
              }
            } catch (refreshError) {
              console.log('Token refresh failed:', refreshError);
              // Just clear tokens silently
              authStore.logout();
            }
          }
        } else {
          // Verify current token and fetch user data
          try {
            await authStore.fetchUserData();
            
            // Check role-based redirection based on user data
            const shouldRedirect = await handleRoleBasedRedirect();
            if (shouldRedirect) {
              return;
            }
            
            // Handle explicit redirect if present
            if (redirectUrl) {
              handleRedirect(redirectUrl);
              return;
            }
            
            // Otherwise navigate to dashboard for OWNER/ADMIN roles
            navigate('/dashboard');
          } catch (fetchError) {
            console.log('User data fetch failed:', fetchError);
            // Just clear tokens silently
            authStore.logout();
          }
        }
      } finally {
        setIsCheckingSession(false);
        setSessionChecked(true);
      }
    };

    checkActiveSession();
  }, [navigate, isCheckingSession, sessionChecked, getRedirectUrl, handleRedirect, handleRoleBasedRedirect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await authStore.login(email, password);
      
      // Check role-based redirection after login
      if (authStore.user && authStore.currentOrganization) {
        const shouldRedirect = await handleRoleBasedRedirect();
        if (shouldRedirect) {
          return;
        }
      }
      
      // Handle explicit redirect if present
      const redirectUrl = getRedirectUrl();
      if (redirectUrl) {
        handleRedirect(redirectUrl);
        return;
      }
      
      // Otherwise use standard navigation for OWNER/ADMIN roles
      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate('/dashboard');
      }
    } catch (loginError) {
      // Error handled by store
      console.log('Login failed:', loginError);
    }
  };

  return (
    <main className="grid min-h-screen place-items-center px-4 py-8">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-bold">Welcome to {import.meta.env.VITE_APP_NAME || 'SensorLab'}</h1>
          <p className="text-gray-500">{t('auth.signIn')}</p>
          <div className="flex justify-center pt-2">
            <LanguageSwitcher />
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant="outline"
              className="w-full bg-white"
              onClick={() => authStore.googleLogin()}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <title>Google logo</title>
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google
            </Button>
            <Button
              variant="outline"
              className="w-full bg-white"
              onClick={() => authStore.microsoftLogin()}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <title>Microsoft logo</title>
                <path
                  d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z"
                  fill="#00A4EF"
                />
              </svg>
              Microsoft
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or continue with email
              </span>
            </div>
          </div>

          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {authStore.error && (
                  <Alert variant="destructive">
                    <AlertDescription>{authStore.error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.email')}
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Input
                    id="password"
                    type="password"
                    placeholder={t('auth.password')}
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="flex items-center justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>

                <Button type="submit" className="w-full" disabled={authStore.loading}>
                  {authStore.loading ? t('common.loading') : t('auth.signIn')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="text-center text-sm">
          <span className="text-gray-500">{t('auth.dontHaveAccount')} </span>
          <Link to="/register" className="text-blue-600 hover:underline font-medium">
            {t('auth.register')}
          </Link>
        </div>

        <p className="text-center text-xs text-gray-500">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-blue-600 hover:underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-blue-600 hover:underline">
            Privacy Policy
          </Link>
        </p>
      </div>
    </main>
  );
});

export default LoginPage; 